import PropTypes from 'prop-types'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import * as CardRelationTypes from 'const/CardRelationTypes'
import KanBoIcon from 'components/KanBoIcon'
import {
  ToggleModal
} from 'components/Modal'
import AddCardRelation from './AddCardRelation'
import { withBoardPermission } from 'dataProviders/Board'
import {
  ContentHeader,
  ContentIcon,
  ContentTitle,
  ContentButton,
  ContentBody,
} from 'components/Content'
import Content from '../../Content'
import ElementContentIcon from '../../ContentIcon'
import styled from 'styled-components'
import SmartCard from 'components/SmartCard'
import { CardValue } from 'components/Card'

const CardContainer = styled(ContentBody)`
  padding: 10px;
  margin: 10px;
  margin-top: 0;
  background-color: #eceff1;
  border-radius: 5px;
  box-shadow: inset 0 0 3px 0px rgba(0, 0, 0, 0.3);

  & > * {
    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
`

const RelationGroupHeader = styled(ContentHeader)`
  padding-bottom: 0;
  min-height: 0;
  border-top: 1px solid #eceff1;
`

const cardRelationsIconNameMap = {
  [CardRelationTypes.CHILD]: 'kanbo-16-relation-child',
  [CardRelationTypes.PARENT]: 'kanbo-16-relation-parent',
  [CardRelationTypes.SUCCESSOR]: 'kanbo-16-relation-successor',
  [CardRelationTypes.PREDECESSOR]: 'kanbo-16-relation-predecessor'
}

const cardRelationsFormattedMessageMap = {
  [CardRelationTypes.CHILD]: 'CARD_RELATION_CHILD',
  [CardRelationTypes.PARENT]: 'CARD_RELATION_PARENT',
  [CardRelationTypes.SUCCESSOR]: 'CARD_RELATION_SUCCESSOR',
  [CardRelationTypes.PREDECESSOR]: 'CARD_RELATION_PREDECESSOR'
}

export class CardRelations extends React.Component {
  static propTypes = {
    permission: PropTypes.object.isRequired,

    id: PropTypes.number.isRequired,
    isArchived: PropTypes.bool.isRequired,
    relations: PropTypes.array.isRequired,
    defaultParentId: PropTypes.number,

    requestRemoveCardParent: PropTypes.func.isRequired,
    requestRemoveCardSequence: PropTypes.func.isRequired,
    requestChangeCardDefaultParent: PropTypes.func.isRequired,
  }

  state = {
    isVisibleByType: {
      [CardRelationTypes.CHILD]: false,
      [CardRelationTypes.PARENT]: true,
      [CardRelationTypes.SUCCESSOR]: true,
      [CardRelationTypes.PREDECESSOR]: true,
    }
  }

  closeToggleModal = () => {
    this.toggleModalRef.close()
  }

  handleToggleModalRef = (ref) => {
    this.toggleModalRef = ref
  }

  render () {
    const {
      permission,
      id,
      isArchived,
      relations,
      defaultParentId,

      requestRemoveCardParent,
      requestRemoveCardSequence,
      requestChangeCardDefaultParent,
    } = this.props
    const {
      isVisibleByType,
    } = this.state

    return (
      <Content>
        <ContentHeader>
          <ElementContentIcon>
            <KanBoIcon name='kanbo-24-card-relations' />
          </ElementContentIcon>
          <ContentTitle>
            <FormattedMessage id='CARD_RELATIONS' />
          </ContentTitle>
        </ContentHeader>
        {relations.map(relationGroup =>
          <React.Fragment
            key={relationGroup.type}
          >
            <RelationGroupHeader>
              <ContentIcon>
                <KanBoIcon name={cardRelationsIconNameMap[relationGroup.type]} />
              </ContentIcon>
              <ContentTitle>
                <FormattedMessage id={cardRelationsFormattedMessageMap[relationGroup.type]}/>
              </ContentTitle>
              <ContentIcon
                onClick={() => {
                  this.setState(prevState => ({
                    isVisibleByType: {
                      ...prevState.isVisibleByType,
                      [relationGroup.type]: !prevState.isVisibleByType[relationGroup.type]
                    }
                  }))
                }}
              >
                <KanBoIcon name={isVisibleByType[relationGroup.type] ? 'kanbo-16-chevron-down' : 'kanbo-16-chevron-left'} />
              </ContentIcon>
            </RelationGroupHeader>
            {(isVisibleByType[relationGroup.type] && relationGroup.cardIds.length > 0) &&
              <CardContainer>
                {relationGroup.cardIds.map(cardId => {
                  const isDefaultParent = cardId === defaultParentId

                  const showDefaultParentOptions = (
                    relationGroup.type === CardRelationTypes.PARENT &&
                    relationGroup.cardIds.length > 1
                  )

                  let menu

                  if (!isArchived && permission.member) {
                    menu = []

                    if (showDefaultParentOptions && !isDefaultParent) {
                      menu.push({
                        icon: <KanBoIcon fixedWidth />,
                        title: <FormattedMessage id='CARD_RELATION_PARENT_SET_DEFAULT' />,
                        onClick: () => {
                          requestChangeCardDefaultParent({
                            cardId: id,
                            defaultParentId: cardId,
                          })
                        }
                      })
                    }

                    menu.push({
                      icon: <KanBoIcon fixedWidth name='kanbo-16-remove' />,
                      title: <FormattedMessage id='REMOVE' />,
                      onClick: () => {
                        switch (relationGroup.type) {
                          case CardRelationTypes.CHILD:
                            requestRemoveCardParent({
                              cardId,
                              parentId: id
                            })
                            break
                          case CardRelationTypes.PARENT:
                            requestRemoveCardParent({
                              cardId: id,
                              parentId: cardId,
                            })
                            break
                          case CardRelationTypes.SUCCESSOR:
                            requestRemoveCardSequence({
                              predecessorId: id,
                              successorId: cardId,
                            })
                            break
                          case CardRelationTypes.PREDECESSOR:
                            requestRemoveCardSequence({
                              predecessorId: cardId,
                              successorId: id
                            })
                            break
                          default:
                            break
                        }
                      }
                    })
                  }

                  return (
                    <SmartCard
                      key={cardId}
                      id={cardId}
                      menu={menu}
                      values={
                        (showDefaultParentOptions && isDefaultParent)
                          ? (
                            <CardValue>
                              <FormattedMessage id='CARD_RELATION_PARENT_DEFAULT_LABEL' />
                            </CardValue>
                          )
                          : undefined
                      }
                    />
                  )
                })}
              </CardContainer>
            }
          </React.Fragment>
        )}
        {(!isArchived && permission.member) &&
          <ToggleModal
            backdrop
            ref={this.handleToggleModalRef}
            attachment='top right'
            targetAttachment='top right'
            constraints={[
              {
                to: 'window',
                pin: true
              }
            ]}
          >
            <ContentButton
              onClick={this.handleAddItemClick}
            >
              <ContentIcon>
                <KanBoIcon name='kanbo-16-plus' />
              </ContentIcon>
              <ContentTitle>
                <FormattedMessage id='CARD_RELATION_ADD' />
              </ContentTitle>
            </ContentButton>
            <div style={{ width: 300, maxHeight: 300 }}>
              <AddCardRelation
                onClose={this.closeToggleModal}
              />
            </div>
          </ToggleModal>
        }
      </Content>
    )
  }
}

export default withBoardPermission(CardRelations)
